import { Injectable } from "@angular/core";

/**
 * Reusable helper for scrolling to the first invalid control of a form.
 *
 * Typical usage on submit:
 *
 * ```ts
 * submit(): void {
 *   if (this.form.invalid) {
 *     this.form.markAllAsTouched();
 *     this._formScroll.scrollToFirstInvalidControl(this._elementRef.nativeElement);
 *     return;
 *   }
 *   // ...
 * }
 * ```
 */
@Injectable({ providedIn: "root" })
export class FormScrollService {
  /** Angular sets `.ng-invalid` on the host of every invalid control (including custom CVA components). */
  private readonly invalidControlSelector = ".ng-invalid";

  /**
   * Smoothly scrolls to (and focuses) the first invalid control inside the given container.
   *
   * @param container Element to search within. Defaults to the whole document.
   * @returns `true` when an invalid control was found and scrolled to.
   */
  scrollToFirstInvalidControl(container?: HTMLElement | null): boolean {
    const root: ParentNode = container ?? document;
    const invalidControls = Array.from(root.querySelectorAll<HTMLElement>(this.invalidControlSelector));
    // Skip wrapping form/formGroup hosts - keep the actual fields only.
    const firstInvalid = invalidControls.find((el) => el.tagName !== "FORM" && !el.hasAttribute("formGroupName"));

    if (!firstInvalid) {
      return false;
    }

    firstInvalid.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    this._focusElement(firstInvalid);

    return true;
  }

  private _focusElement(element: HTMLElement): void {
    const focusableSelector = "input, select, textarea, [tabindex]";
    const focusable = element.matches(focusableSelector) ? element : element.querySelector<HTMLElement>(focusableSelector);

    // preventScroll keeps the smooth scroll above from being overridden by focus().
    focusable?.focus({ preventScroll: true });
  }
}
